// **React Imports
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// **MUI Imports
import { Box, Card, Grid } from '@mui/material'

// **Components Imports
import CustomChip from '../../components/chip'
import CustomDataGrid from '../../components/data-grid'
import OverlayLoading from '../../components/overlay-loading'
import SEOComponent from '../../seo'

// **Utils Imports
import { formatDateTime, showErrorToast } from '../../utils/utils'

// **API Imports
import {
  getCouponById,
  getCoupons,
  getTenantList
} from '../../axios/api/developer'

// **Third party Imports

// **SCSS Imports
import Icon from '../../components/icon'
import AddUpdateCouponDialog from './AddUpdateCouponDialog'
import CouponHistoryModal from './CouponHistoryModal'
import './index.scss'
import TableHeader from './TableHeader'

// columns
const columns = (
  tenantList = [],
  handleEdit,
  handleOpenHistoryModal,
  handleClone
) => {
  return [
    {
      flex: 0.15,
      display: 'flex',
      minWidth: 120,
      field: 'code',
      headerName: 'Coupon Code'
    },
    {
      flex: 0.15,
      display: 'flex',
      minWidth: 120,
      field: 'isGlobal',
      headerName: 'Accessibility',
      renderCell: ({ row, value }) => (
        <CustomChip
          label={
            value
              ? 'GLOBAL'
              : `${
                  tenantList?.find((x) => x?.id === row?.tenantId)?.name || '-'
                }`
          }
          color={value ? 'default' : 'primary'}
          rounded
          variant="outlined"
          size="small"
        />
      )
    },
    {
      flex: 0.12,
      display: 'flex',
      textAlign: 'center',
      alignContent: 'center',
      minWidth: 100,
      field: 'isOneTimeUse',
      headerName: 'Usage',
      renderCell: ({ value }) => (
        <CustomChip
          label={value ? 'ONE TIME' : 'NO LIMIT'}
          color={value ? 'default' : 'primary'}
          rounded
          size="small"
        />
      )
    },
    {
      flex: 0.15,
      display: 'flex',
      minWidth: 100,
      field: 'startTs',
      headerName: 'Start Date',
      renderCell: ({ value }) => (value ? formatDateTime(value) : '-')
    },
    {
      flex: 0.15,
      display: 'flex',
      minWidth: 100,
      field: 'expirationDate',
      headerName: 'Expiration Date',
      renderCell: ({ value }) => (value ? formatDateTime(value) : '-')
    },
    {
      flex: 0.1,
      display: 'flex',
      textAlign: 'center',
      alignContent: 'center',
      minWidth: 80,
      field: 'status',
      headerName: 'Status',
      renderCell: ({ value }) => (
        <CustomChip
          label={value}
          color={
            value === 'ACTIVE'
              ? 'success'
              : value === 'EXPIRED'
              ? 'error'
              : 'default'
          }
          rounded
          size="small"
        />
      )
    },
    {
      flex: 0.15,
      display: 'flex',
      minWidth: 100,
      field: 'createdTs',
      headerName: 'Created At',
      renderCell: ({ value }) => (value ? formatDateTime(value) : '-')
    },
    {
      flex: 0.15,
      minWidth: 150,
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Icon
            hoverEffect
            icon="mdi:edit"
            title="Edit"
            onClick={() => handleEdit(row)}
          />
          <Icon
            hoverEffect
            icon="mdi:content-copy"
            title="Clone"
            onClick={() => handleClone(row)}
          />
          <Icon
            hoverEffect
            icon="mdi:history"
            title="Usage History"
            onClick={() => handleOpenHistoryModal(row)}
          />
        </Box>
      )
    }
  ]
}

const Coupons = () => {
  // states
  const [loading, setLoading] = useState({
    couponsList: false,
    tenants: false
  })
  const [coupons, setCoupons] = useState(null)
  const [tenants, setTenants] = useState([])
  const [couponModal, setCouponModal] = useState({
    open: false,
    isEdit: false,
    data: null
  })
  const [historyModal, setHistoryModal] = useState({
    open: false,
    config: null
  })
  const [totalCouponsCount, setTotalCouponsCount] = useState({
    totalElements: 0,
    totalPages: 0
  })
  const [filters, setFilters] = useState({
    page: 0,
    size: 10,
    isGlobal: null,
    isOneTimeUse: null,
    search: '',
    sortOrder: 'desc',
    sortBy: 'createdTs',
    toDate: null,
    fromDate: null,
    couponStatus: ''
  })

  // fetch functions
  const fetchTentants = useCallback(async () => {
    setLoading((prev) => ({ ...prev, tenants: true }))
    const res = await getTenantList()
    setLoading((prev) => ({ ...prev, tenants: false }))
    if (res?.isError) {
      showErrorToast(res)
      return
    }
    setTenants(res || [])
  }, [])

  const fetchData = useCallback(async () => {
    const config = filters || {}

    const payload = {
      page: config?.page || 0,
      size: config?.size || 10,
      isGlobal: config?.isGlobal ?? null,
      isOneTimeUse: config?.isOneTimeUse ?? null,
      search: config?.search || null,
      sortOrder: config?.sortOrder?.toUpperCase() || 'DESC',
      sortBy: config?.sortBy || 'code',
      toDate: config?.toDate || null,
      fromDate: config?.fromDate || null,
      couponStatus: config?.couponStatus || null
    }

    setLoading((prev) => ({ ...prev, couponsList: true }))
    const response = await getCoupons(payload)
    setLoading((prev) => ({ ...prev, couponsList: false }))

    if (response?.isError) {
      setCoupons(null)
      showErrorToast(response)
    } else {
      setCoupons(response?.coupons)
      setTotalCouponsCount({
        totalElements: response?.totalElements,
        totalPages: response?.totalPages
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)])

  // handlers
  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key === 'pageSize' ? 'size' : key]: value,
      ...(key === 'pageSize' || key === 'search' ? { page: 0 } : {})
    }))
  }, [])

  const handleSortModelChange = useCallback((sortModel) => {
    setFilters((prev) => {
      const model = sortModel?.[0]
      return {
        ...prev,
        sortBy: model?.field || prev.sortBy || 'createdTs',
        sortOrder: model?.sort || prev.sortOrder
      }
    })
  }, [])

  const handleFilterChange = useCallback((value) => {
    setFilters((prev) => ({ ...prev, ...value, page: 0 }))
  }, [])

  const handleOpenCouponModal = useCallback(async (data = null) => {
    const isEdit = data ? true : false
    let couponConfig = null

    if (data?.id) {
      const res = await getCouponById(data?.id)
      if (res?.isError) {
        showErrorToast(res)
        return
      }
      couponConfig = res
    }

    setCouponModal((prev) => ({
      ...prev,
      open: true,
      data: couponConfig,
      isEdit: isEdit
    }))
  }, [])

  const handleCloneCoupon = useCallback(async (data) => {
    let couponConfig = data || null
    if (data?.id) {
      const res = await getCouponById(data?.id)
      if (res?.isError) {
        showErrorToast(res)
        return
      }
      couponConfig = res
    }

    const cloneConfig = {
      ...couponConfig,
      code: '',
      id: null
    }

    setCouponModal((prev) => ({
      ...prev,
      open: true,
      data: cloneConfig,
      isEdit: false
    }))
  }, [])

  const handleOpenHistoryModal = useCallback(async (data) => {
    setHistoryModal((prev) => ({
      ...prev,
      open: true,
      config: {
        code: data?.code,
        tenantId: data?.tenantId
      }
    }))
  }, [])

  // columns
  const memorisedColumns = useMemo(() => {
    return columns(
      tenants,
      handleOpenCouponModal,
      handleOpenHistoryModal,
      handleCloneCoupon
    )
  }, [
    tenants,
    handleOpenCouponModal,
    handleOpenHistoryModal,
    handleCloneCoupon
  ])

  // effects
  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    fetchTentants()
  }, [fetchTentants])

  return (
    <>
      {loading?.tenants && <OverlayLoading />}
      <SEOComponent title={'Tenant Invoices | CoderScout'} />
      <Grid container className="coupons-main-container" spacing={6.5}>
        <Grid item xs={12}>
          <TableHeader
            filters={filters}
            handleChange={handleFilterChange}
            handleOpenCouponModal={handleOpenCouponModal}
          />
          <Card>
            <CustomDataGrid
              className="coupons-table"
              sortingMode="server"
              filterMode="server"
              paginationMode="server"
              loading={loading?.couponsList}
              rows={coupons || []}
              columns={memorisedColumns}
              pageSizeValue={filters?.size}
              pageValue={filters?.page}
              sortingOrder={['asc', 'desc']}
              sortModel={[
                {
                  field: filters?.sortBy,
                  sort: filters?.sortOrder
                }
              ]}
              onSortModelChange={handleSortModelChange}
              pageCount={totalCouponsCount?.totalPages}
              rowCount={totalCouponsCount?.totalElements}
              handlePaginationChange={handleChange}
            />
          </Card>
        </Grid>
      </Grid>

      {couponModal?.open && (
        <AddUpdateCouponDialog
          open={couponModal?.open}
          config={couponModal?.data}
          isEdit={couponModal?.isEdit}
          tenants={tenants}
          handleRefresh={fetchData}
          onClose={() => {
            setCouponModal({ open: false, data: null, isEdit: false })
          }}
        />
      )}

      {historyModal?.open && (
        <CouponHistoryModal
          open={historyModal?.open}
          code={historyModal?.config?.code}
          tenantId={historyModal?.config?.tenantId}
          tenantList={tenants}
          onClose={() => {
            setHistoryModal({ open: false, config: null })
          }}
        />
      )}
    </>
  )
}

export default memo(Coupons)
